import { useEffect } from 'react';
import clsx from 'clsx';

import Icon, { IconSize } from '@components/icon/Icon';
import Tooltip from '@components/tooltip/Tooltip';
import {
  ButtonLabelProps,
  ButtonProps,
  ButtonVariant,
  IconButtonSize
} from '@types';

import Spinner from '../spinner/Spinner';

import theme from '../../../themes/colors';

const wrapperStyles = `flex items-center rounded-lg justify-center text-body-semi disabled:cursor-not-allowed`;

const variantStyles = {
  primary: `bg-primary disabled:bg-grey40 hover:bg-primary60`,
  transparent: `bg-transparent`,
  secondary: `bg-primaryLite1 border-primary disabled:bg-white disabled:border-grey30 border hover:border-primary60 hover:bg-white`,
  tertiary: `bg-theme border-grey40 border disabled:border-grey30 disabled:bg-grey10`,
  secondaryRed: `bg-error30 border-error20 border disabled:border-grey40 disabled:bg-grey10`,
  secondaryGreen: `bg-primaryLite4 border-primary50 border disabled:bg-bgGrey10 disabled:border-grey`
};

const loadingStyles = {
  primary: `!bg-primary`,
  transparent: '',
  secondary: '!bg-primaryLite1 border-primary border',
  tertiary: '',
  secondaryRed: '',
  secondaryGreen: ''
};

const labelStyles = {
  primary: {
    default: 'text-base text-theme font-medium',
    disabled: '!text-grey'
  },
  transparent: {
    default: 'text-base text-primary font-medium',
    disabled: '!text-grey'
  },
  secondary: {
    default: 'text-sm text-primary font-semibold',
    disabled: '!text-grey40'
  },
  tertiary: {
    default: 'text-sm text-content font-semibold',
    disabled: 'text-grey font-semibold text-sm'
  },
  secondaryRed: {
    default: 'text-sm font-medium text-error20',
    disabled: 'text-grey font-semibold text-sm'
  },
  secondaryGreen: {
    default: 'text-sm font-medium text-primary50',
    disabled: '!text-grey40'
  }
};

const loadingLabelStyles = {
  primary: 'text-white text-body-semi invisible',
  transparent: 'invisible',
  secondary: 'invisible',
  tertiary: '',
  secondaryRed: 'invisible',
  secondaryGreen: 'invisible'
};

const sizeStyles = {
  none: '',
  small: 'px-3 py-1.5 h-9',
  medium: 'px-4 py-2.5 h-11',
  large: 'p-4 h-11',
  full: 'w-full'
};

const iconSizeStyles = {
  none: 'hover:bg-primary p-0.5 ',
  small: 'hover:bg-primary p-1.5',
  medium: 'p-2.5',
  large: 'p-4',
  full: ''
};

export const ButtonLabel: React.FC<ButtonLabelProps> = ({
  variant = 'primary',
  label,
  className,
  loading,
  disabled,
  labelCount
}) => (
  <span
    className={clsx(
      loading ? loadingLabelStyles[variant] : labelStyles[variant].default,
      className,
      disabled && labelStyles[variant].disabled
    )}
  >
    {label}
    {!!labelCount && (
      <span className="ml-2 rounded-full bg-grey30 px-1.5 py-0.5 text-sm font-medium">
        {labelCount}
      </span>
    )}
  </span>
);

const Button: React.FC<ButtonProps> = ({
  handleClick,
  variant = 'primary',
  size = 'medium',
  label = '',
  className,
  children,
  disabled,
  onHover,
  loading,
  leftIconName = '',
  rightIconName = '',
  leftIconStyle,
  leftIconStroke,
  rightIconStroke,
  rightIconStyle,
  rightIconFill,
  leftIconFill,
  labelStyle,
  onEnterEnabled = false,
  labelCount,
  tooltipText = '',
  ...props
}) => {
  const handleMouseEnter = () => onHover && onHover(true);
  const handleMouseLeave = () => onHover && onHover(false);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !disabled && !loading && handleClick) {
        if (onEnterEnabled) handleClick(event);
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleClick, disabled, loading, onEnterEnabled]);

  const getIconStrokeColor = (stroke: string) => {
    let strokeColor: string = stroke;
    if (disabled && variant === 'primary') strokeColor = theme.grey;
    else if (disabled && variant === 'secondary') strokeColor = theme.grey40;
    else if (disabled && variant === 'tertiary') strokeColor = theme.grey;
    return strokeColor;
  };

  return (
    <>
      <button
        className={clsx(
          loading
            ? `relative ${loadingStyles[variant]}`
            : variantStyles[variant],
          sizeStyles[size],
          className,
          wrapperStyles
        )}
        onClick={handleClick}
        type="button"
        disabled={disabled || loading}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-tooltip-id={`button-${tooltipText}`}
        {...props}
      >
        {!!leftIconName && (
          <Icon
            name={leftIconName}
            stroke={getIconStrokeColor(leftIconStroke || '')}
            fill={leftIconFill}
            size="medium"
            className={`mr-2 ${leftIconStyle || ''}`}
          />
        )}
        {label && (
          <ButtonLabel
            variant={variant}
            label={label}
            disabled={disabled}
            loading={loading}
            className={labelStyle || ''}
            labelCount={labelCount}
          />
        )}
        {!!rightIconName && (
          <Icon
            name={rightIconName}
            stroke={getIconStrokeColor(rightIconStroke || '')}
            fill={rightIconFill}
            size="medium"
            className={clsx('ml-2 transition-all duration-200', rightIconStyle)}
          />
        )}
        {loading && (
          <div className="absolute text-center text-theme">
            <Spinner className="size-6 border border-theme" />
          </div>
        )}
        {children}
      </button>
      <Tooltip
        id={`button-${tooltipText}`}
        type="text"
        tooltipText={tooltipText}
      />
    </>
  );
};
export default Button;

interface IconButtonProps extends ButtonProps {
  variant?: ButtonVariant;
  iconName: string;
  toolTipText?: string;
  stroke?: string;
  buttonSize?: IconButtonSize;
  iconSize?: IconSize;
  fill?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  iconName,
  variant = 'primary',
  stroke = '',
  buttonSize = 'large',
  iconSize = 'medium',
  fill,
  ...props
}) => (
  <Button
    variant={variant}
    className={iconSizeStyles[buttonSize]}
    size={buttonSize}
    {...props}
  >
    <Icon name={iconName} size={iconSize} stroke={stroke} fill={fill} />
  </Button>
);
